import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';

import { Theme } from 'ui_system/theme';

// Have used css property for underlining text
// u tag is depreciated and not parsed
const Underline = Quill.import('formats/underline');
class CustomUnderline extends Underline {
  static formats() {
    return true;
  }
}

CustomUnderline.tagName = 'span';
CustomUnderline.className = 'text-underline';
Quill.register({ 'formats/underline': CustomUnderline });

const CustomToolbar = ({ supportImages }) => {
  return (
    <div id="toolbar">
      <span className="ql-formats">
        <button title="Bold" className="ql-bold" />
        <button title="Italic" className="ql-italic" />
        <button title="Underline" className="ql-underline" />
      </span>
      <span className="ql-formats">
        <button title="Numbered list" className="ql-list" value="ordered" />
        <button title="Bulleted list" className="ql-list" value="bullet" />
        <button title="Link" className="ql-link" />
      </span>
      <span className="ql-formats">
        <button title="Remove formatting" className="ql-clean" />
      </span>
      {supportImages && (
        <span className="ql-formats">
          <button title="Image upload" className="ql-image" />
        </span>
      )}
    </div>
  );
};

const RTWrapper = styled('div')`
  width: 100%;
  height: ${(props) => props.isReply && '100%'};
  .ql-container,
  .ql-toolbar {
    border: ${(props) =>
      props.isReply ? 'none' : `solid 1px ${Theme.colors.inputs.border}`};
    margin: ${(props) => (props.isReply ? '15px 35px' : '7px 10px')};
    border: none;
  }
  .ql-editor {
    font-family: 'soleil';
    min-height: ${(props) =>
      props.minHeight ? `${props.minHeight}px` : '150px'};
    max-height: ${(props) => props.isReply && '350px'};
    overflow-y: auto;
  }
  .ql-tooltip.ql-editing.ql-flip {
    left: 0;
  }
  .text-underline {
    text-decoration: underline;
  }
  .ql-toolbar {
    box-shadow: 0 2px 4px #e0e0e0;
    clip-path: inset(-8px -8px -8px -8px);
    border-radius: 3px;
    position: sticky;
    top: 15px;
  }
  .ql-editor.ql-blank::before {
    font-style: normal;
  }
  @media (max-width: ${Theme.mobile.phoneMaxWidth - 1}px) {
    .quill {
      height: ${(props) => props.isReply && '75%'};
    }
    .ql-container,
    .ql-toolbar {
      margin: ${(props) => props.isReply && '0'};
    }
    .ql-editor {
      min-height: ${(props) =>
        props.minHeight ? `${props.minHeight}px` : '150px'};
      max-height: ${(props) => props.isReply && '350px'};
    }
  }
`;

const RichText = (props) => {
  const {
    field,
    handleChange,
    supportImages,
    minHeight,
    bounds,
    shouldAutoFocus = false,
    placeholder = '',
    isReply = false,
  } = props;
  const inputRef = useRef('');
  const modules = {
    toolbar: {
      container: `#toolbar`,
    },
  };

  useEffect(() => {
    shouldAutoFocus && inputRef.current.focus();
  }, [shouldAutoFocus]);

  const handleTextChange = (text) => {
    if (text.replace(/(<([^>]+)>)/gi, '') === '') {
      text = '';
    }

    // remove class ql-indent-4 to restrict sub bullet points to three level only throughout rich editor
    const element = document.getElementsByClassName('ql-indent-4');
    const italicText = document.getElementsByTagName('em');
    if (element.length > 0) {
      element[0].classList.remove('ql-indent-4');
    }
    if (italicText.length > 0) {
      for (let i = 0; i < italicText.length; i++)
        italicText[i].classList.add('italic-text');
    }
    handleChange(field.name, text);
  };

  return (
    <RTWrapper
      minHeight={minHeight}
      isReply={isReply}
      data-text-editor="form-editor"
    >
      <CustomToolbar supportImages={supportImages} />
      <ReactQuill
        onChange={(text) => handleTextChange(text)}
        defaultValue={field.value.replace(/\n/g, '')}
        modules={modules}
        bounds={bounds || 'document.body'}
        ref={inputRef}
        placeholder={placeholder}
      />
    </RTWrapper>
  );
};

RichText.propTypes = {
  field: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
  supportImages: PropTypes.bool,
};

RichText.defaultProps = {
  supportImages: true,
};

export default RichText;
